import React, { useEffect, useState } from "react"
import { ShopifyConnectForm } from "./ShopifyConnectForm"
import { ConnectionStatusBadge } from "./ConnectionStatusBadge"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const primaryBtn =
  "inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const secondaryBtn =
  "inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const dangerBtn =
  "inline-flex items-center justify-center rounded-lg border border-red-200 dark:border-red-900/50 px-5 py-3 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"

const fmtDate = (iso, fallback) =>
  iso ? new Date(iso).toLocaleString() : fallback

// Standalone connection panel: view status, re-connect (rotate token), or
// disconnect. Status comes from GET /shopify_connection (404 => not connected);
// the connect probe is synchronous so no polling is needed.
export function ConnectionSettings({ connectionPath, partnerId, copy }) {
  const [connection, setConnection] = useState(null) // null = not connected
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)
  const [error, setError] = useState(null)
  // Optimistic flag set the moment we trigger a sync (manually, or implicitly on
  // connect) so the UI reflects "syncing" before the server's first poll lands.
  const [triggered, setTriggered] = useState(false)
  const [syncNote, setSyncNote] = useState(null)

  const syncPath = `${connectionPath}/sync`
  const serverSyncing = connection?.sync?.state === "running"
  const syncing = serverSyncing || triggered

  const fetchConnection = () =>
    fetch(connectionPath, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : null))

  useEffect(() => {
    let active = true
    fetchConnection()
      .then((data) => active && setConnection(data))
      .catch(() => active && setError(copy.errorGeneric))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [connectionPath, copy.errorGeneric])

  // Poll while a sync is in flight: the orchestrator job is async, so refetch the
  // status until the partner-level run reports idle, then drop the optimistic flag.
  useEffect(() => {
    if (!syncing) return undefined
    let active = true
    const id = setInterval(() => {
      fetchConnection()
        .then((data) => {
          if (!active || !data) return
          setConnection(data)
          if (data.sync?.state !== "running") setTriggered(false)
        })
        .catch(() => {})
    }, 3000)
    return () => {
      active = false
      clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing, connectionPath])

  const onConnected = (data) => {
    setConnection(data)
    setShowForm(false)
    setToast(null)
    // Connecting auto-kicks a full sync server-side; reflect that immediately.
    setTriggered(true)
    setSyncNote(copy.sync.connectedStarted)
  }

  const syncNow = async () => {
    if (syncing) return
    setSyncNote(null)
    setError(null)
    try {
      const res = await fetch(syncPath, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}))
        setSyncNote(body.error || copy.sync.throttled)
        return
      }
      if (!res.ok) throw new Error("sync failed")
      setTriggered(true)
      setSyncNote(copy.sync.requested)
    } catch {
      setError(copy.errorGeneric)
    }
  }

  const disconnect = async () => {
    if (busy) return
    if (!window.confirm(copy.panel.disconnectConfirm)) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(connectionPath, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        setConnection(null)
        setShowForm(false)
        setToast(copy.panel.disconnectedToast)
      } else {
        setError(copy.errorGeneric)
      }
    } catch {
      setError(copy.errorGeneric)
    } finally {
      setBusy(false)
    }
  }

  const status = connection?.status || "none"

  const formCopy = { ...copy.connect, errorGeneric: copy.errorGeneric }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.panel.title}</h2>
        {!loading && <ConnectionStatusBadge status={status} label={copy.status[status]} />}
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">…</p>
      ) : connection ? (
        <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">{copy.panel.organizationId}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{connection.organization_id}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">{copy.panel.connectedAt}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{fmtDate(connection.connected_at, copy.panel.never)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">{copy.panel.lastVerifiedAt}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{fmtDate(connection.last_verified_at, copy.panel.never)}</dd>
          </div>
          {connection.last_error && (
            <div className="sm:col-span-2">
              <dt className="text-gray-500 dark:text-gray-400">{copy.panel.lastError}</dt>
              <dd className="text-red-600 dark:text-red-400">{connection.last_error}</dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{copy.panel.none}</p>
      )}

      {connection && syncing && (
        <div
          role="status"
          className="mt-4 flex items-center gap-2 rounded-lg border border-brand-200 dark:border-brand-900/50 bg-brand-50 dark:bg-brand-950/40 px-4 py-3 text-sm text-brand-700 dark:text-brand-300"
        >
          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand-500" aria-hidden="true" />
          {syncNote || copy.sync.syncing}
        </div>
      )}

      {toast && <p className="mt-4 text-sm text-green-700 dark:text-green-300">{toast}</p>}
      {syncNote && !syncing && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{syncNote}</p>}
      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {!loading && (
        <div className="mt-6">
          {showForm ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">{copy.panel.reconnectHint}</p>
              <ShopifyConnectForm
                connectPath={connectionPath}
                copy={formCopy}
                partnerId={partnerId}
                initialOrgId={connection?.organization_id || ""}
                onSuccess={onConnected}
                onCancel={() => setShowForm(false)}
                cancelLabel={copy.panel.cancel}
              />
            </div>
          ) : (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              {connection && (
                <button type="button" onClick={disconnect} disabled={busy} className={dangerBtn}>
                  {busy ? copy.panel.disconnecting : copy.panel.disconnect}
                </button>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {connection && (
                  <button type="button" onClick={syncNow} disabled={syncing} className={secondaryBtn}>
                    {syncing ? copy.sync.syncing : copy.sync.now}
                  </button>
                )}
                <button type="button" onClick={() => setShowForm(true)} className={primaryBtn}>
                  {connection ? copy.panel.reconnect : copy.connect.submit}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
